
import { useEffect, useRef, useCallback } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { debounce } from '@/lib/hashUtils';

/**
 * Custom hook to sync form data to parent state with debouncing
 * @param watch - React Hook Form watch function
 * @param formData - Current form data object
 * @param updateFormData - Function to update parent form data
 * @param delay - Debounce delay in milliseconds (default: 2000ms)
 */
export function useFormSyncToHash<T extends Record<string, any>>(
  watch: UseFormWatch<any>,
  formData: T,
  updateFormData: (data: Partial<T>) => void,
  delay: number = 500
): void {
  // Watch all fields in the form
  const watchedFields = watch();
  
  // Store previous watched fields to compare for actual changes
  const prevWatchedFieldsRef = useRef<any>(null);
  
  // Create a stable debounced function
  const debouncedUpdate = useCallback(
    debounce((changedFields: Partial<T>) => {
      console.log('Syncing form data to hash:', changedFields);
      updateFormData(changedFields);
    }, delay),
    [updateFormData, delay]
  );
  
  useEffect(() => {
    // Skip if this is the first render or no previous data to compare
    if (!prevWatchedFieldsRef.current) {
      prevWatchedFieldsRef.current = watchedFields;
      return;
    }
    
    // Deep comparison to find actually changed fields
    const changedFields = Object.entries(watchedFields).reduce((acc, [key, value]) => {
      const formKey = key as keyof T;
      const prevValue = prevWatchedFieldsRef.current?.[key];
      
      // Only include fields that have actually changed (deep comparison for strings)
      if (value !== prevValue && value !== formData[formKey]) {
        acc[formKey] = value as T[keyof T];
      }
      return acc;
    }, {} as Partial<T>);
    
    // Only update if we have actual changes
    if (Object.keys(changedFields).length > 0) {
      debouncedUpdate(changedFields);
    }
    
    // Update the previous fields reference
    prevWatchedFieldsRef.current = watchedFields;
    
  }, [watchedFields, formData, debouncedUpdate]);
}
